import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Head from './layouts/Head';
import LoadingDashboardContent from '../components/LoadingDashboardContent';
import { PageList } from '../components/PageComponents';
import { FormGenerator } from '../helper/FormGenerator';
import { API_URL } from '../constants';
import { formatDateTime, idFormat } from '../utils/appUtils';
import { useTokens } from '../context/TokenContext';

const API_BASE = `${API_URL}/ticket`;
const admin = localStorage.getItem('admin_UserInfo') || '{}';

const AdminSupport = () => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [formData, setFormData] = useState({ subject: '', description: '', type: 'support', priority: 'medium', status: 'open' });
  const [replyForm, setReplyForm] = useState({ message: '', status: 'open' });
  const [replies, setReplies] = useState([]);
  const [pageData, setPageData] = useState({ data: [], dataCount: 0 });
  const [isReload, setIsReload] = useState(false);

  const pageType = {
    isNew: pathname.includes('new'),
    isEdit: pathname.includes('edit'),
    isList: !(pathname.includes('new') || pathname.includes('edit')),
  };

  const pageId = pageType.isEdit ? pathname.split('/').pop() : null;

  const queryParams = new URLSearchParams(search);
  const currentPage = Number(queryParams.get('page')) || 1;
  const limit = Number(queryParams.get('limit')) || 20;
  const searchBy = queryParams.get('searchBy') || 'title';
  const searchValue = queryParams.get('searchValue') || '';

  const fetchAllTickets = async () => {
    try {
      const res = await axios.get(`${API_BASE}`, {
        params: { page: currentPage, limit, searchBy, searchValue },
        headers: { Authorization: `Bearer ${adminAuthToken}` },
      });
      setPageData({ data: res.data.data, dataCount: res.data.dataCount });
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch tickets.');
    }
  };

  const fetchTicketById = async () => {
    try {
      const res = await axios.get(`${API_BASE}/${pageId}`, {
        headers: { Authorization: `Bearer ${adminAuthToken}` },
      });

      const data = res.data.data;
      setFormData(data);
      setReplyForm(prev => ({ ...prev, status: data.status || 'open' }));
      setReplies(data.replies || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching ticket.');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      if (pageType.isList) {
        await fetchAllTickets();
      }

      if (pageType.isEdit) {
        await fetchTicketById();
      }

      setLoading(false);
    };

    fetchData();
    
    if (isReload) {
      setIsReload(false);
    }
  }, [pathname, search, isReload]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const toastId = toast.loading(`${pageType.isEdit ? 'Updating' : 'Creating'} ticket...`);

    try {
      const url = pageType.isEdit ? `${API_BASE}/${pageId}` : `${API_BASE}`;
      const method = pageType.isEdit ? 'put' : 'post';
      const res = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${adminAuthToken}` },
      });

      toast.update(toastId, {
        render: `Ticket ${pageType.isEdit ? 'updated' : 'created'} successfully!`,
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      navigate(`/admin/support/edit/${res.data.id}`);
    } catch (error) {
      toast.update(toastId, {
        render: error?.response?.data?.error || 'Failed to save ticket',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
    }
  };

  const handleReplySubmit = async () => {
    if (replyForm.message && !replyForm.message.trim()) return toast.warning('Reply cannot be empty.');
    const toastId = toast.loading('Sending reply...');

    try {
      const res = await axios.post(`${API_BASE}/${pageId}/reply`, replyForm, {
        headers: { Authorization: `Bearer ${adminAuthToken}` },
      });

      setReplyForm(prev => ({ ...prev, message: '' }));

      toast.update(toastId, {
        render: 'Reply sent!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      fetchTicketById();
    } catch (err) {
      toast.update(toastId, {
        render: err?.response?.data?.error || 'Failed to send reply',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;

    const toastId = toast.loading('Deleting...');
    try {
      await axios.delete(`${API_BASE}/${id}`, {
        headers: { Authorization: `Bearer ${adminAuthToken}` },
      });

      toast.update(toastId, { render: 'Deleted successfully', type: 'success', isLoading: false, autoClose: 3000 });
      fetchAllTickets();
    } catch (err) {
      toast.update(toastId, { render: 'Delete failed', type: 'error', isLoading: false, autoClose: 3000 });
    }
  };

  const updateUrlParams = (page, limit, searchBy, searchValue) => {
    const params = new URLSearchParams({ page, limit, searchBy, searchValue });
    navigate({ pathname: '/admin/support/list', search: params.toString() });
  };

  const handleReplyDelete = async (replyId) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) return;

    const toastId = toast.loading('Deleting reply...');

    try {
      const res = await axios.delete(`${API_BASE}/reply/${replyId}`, {
        headers: { Authorization: `Bearer ${adminAuthToken}` },
      });

      toast.update(toastId, {
        render: res.data.status === 'success' ? 'Reply deleted successfully' : 'Error deleting reply',
        type: res.data.status === 'success' ? 'success' : 'error',
        isLoading: false,
        autoClose: 3000,
      });

      if (res.data.status === 'success') fetchTicketById();
    } catch (err) {
      console.error(err);
      toast.update(toastId, {
        render: 'Failed to delete reply',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      <Head title={pageType.isList ? 'Support Tickets' : pageType.isEdit ? 'Edit Ticket' : 'Create Ticket'} />

      {loading ? (
        <LoadingDashboardContent progress={progress} />
      ) : pageType.isList ? (
        <PageList
          itemType="ticket"
          itemId="id"
          pageTitle="Support Ticket"
          pageData={pageData.data}
          totalRows={pageData.dataCount}
          actionApplyKeyMap={{ Delete: 'delete' }}
          columns={['Id', 'Subject', 'Type', 'Created by', 'Created at', 'Status']}
          filterBy={{ '--select--': '', Type: 'type', Status: 'status', ID: 'id' }}
          columnKeyMap={{
            Id: item => idFormat(item.id),
            Subject: item => (
              <Link className="a_edit ms-2" to={`/admin/support/edit/${item.id}`}>{item.subject}</Link>
            ),
            Type: item => item.type,
            'Created by': item => <>{item.craatedBy} {idFormat(item.craatedById)}</>,
            'Created at': item => formatDateTime(item.createdAt),
            Status: item => item.status == 'open'
              ? <span className='badge bg-danger'>{item.status}</span>
              : <span className='badge bg-info'>{item.status}</span>,
          }}
          onAddNew={false}
          onDelete={handleDelete}
          updateUrlParams={updateUrlParams}
          isReload={setIsReload}
        />
      ) : (
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/admin/support')}>
              <i className="bi bi-arrow-left"></i> Back
            </button>
            <h4 className="m-0">{pageType.isEdit ? 'Edit Ticket' : ''}</h4>
          </div>
          <div className='row g-4'>
            <div className='col-md-5'>
              <form onSubmit={handleFormSubmit}>
                <FormGenerator
                  fields={[
                    { id: 'subject', label: 'Subject', type: 'text', required: true },
                    { id: 'message', label: 'Message', type: 'textarea' },
                    {
                      id: 'type',
                      label: 'Type',
                      type: 'select',
                      options: { support: 'Support', complaint: 'Complaint', suggestion: 'Suggestion', dispute: 'Dispute' },
                    },
                  ]}
                  fieldData={formData}
                  onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
                  authToken={adminAuthToken}
                />
                <div className="text-end mt-3">
                  <button className="btn btn-dark" type="submit">
                    {pageType.isEdit ? 'Update Ticket' : 'Create Ticket'}
                  </button>
                </div>
              </form>
            </div>
            <div className='col-md-7'>
              {pageType.isEdit && (
                <div className='card'>
                  <div className='card-body'>
                    <h4 className="mt-0">Replies</h4>
                    <div className="mb-4">
                      {replies.length === 0 ? (
                        <div className="text-muted">No replies yet.</div>
                      ) : (
                        replies.map((reply, idx) => (
                          <div className={reply.userId == JSON.parse(admin).id ? 'text-end ms-5' : 'text-start me-5'} >
                            <div key={idx} className="border rounded p-2 mb-2 bg-light d-inline-block">
                              <strong className='text-primary'>Reply by: {reply.userName || 'User'}</strong>
                              <p className="mb-1">{reply.message}</p>
                              <small className="text-muted">{formatDateTime(reply.createdAt)} </small>
                              <button className='badge bg-danger me-2' onClick={() => handleReplyDelete(reply.id)}><i className='bi bi-trash'></i></button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <h5>Send a Reply</h5>
                    <FormGenerator
                      fields={[
                        { id: 'message', label: 'Reply', type: 'textarea' },
                        {
                          id: 'status',
                          label: 'Status',
                          type: 'select',
                          options: { open: 'Open', closed: 'Closed', resolved: 'Resolved' },
                        },
                      ]}
                      fieldData={replyForm}
                      onChange={(field, value) => setReplyForm(prev => ({ ...prev, [field]: value }))}
                      authToken={adminAuthToken}
                    />
                    <div className="text-end mt-3">
                      <button className="btn btn-primary" onClick={handleReplySubmit}>Send Reply</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </>
  );
};

export default AdminSupport;
